<template>
    <!-- Modal backdrop. This what you want to place close to the closing body tag -->
    <div
        v-show="isModalOpen"
        class="fixed inset-0 z-30 flex items-end bg-black bg-opacity-50 sm:items-center sm:justify-center p-10"
    >
        <!-- Modal -->
        <div
            class="w-full flex flex-col overflow-hidden max-h-full bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:m-4 sm:max-w-5xl"
            role="dialog"
            id="modal"
        >
            <!-- Modal body -->
            <div class="px-6 py-4 border-b dark:border-gray-500 flex justify-between items-center">
                <p class="text-lg font-semibold text-gray-700 dark:text-gray-300">Select Element</p>
                <jet-input
                    v-model="search"
                    type="search"
                    class="block w-1/3 text-sm dark:border-gray-600 dark:bg-gray-700 focus:border-red-400 focus:outline-none focus:shadow-outline-red dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                    placeholder="Search Elements"
                    ref="searchInput"
                />
            </div>
            <div class="px-6 py-4 border-b dark:border-gray-500 flex justify-between items-center">
                <div class="grid gap-6 md:grid-cols-3">
                    <label v-for="(searchGroupName, searchGroup) in blockSearchGroup"
                           class="cursor-pointer flex justify-center text-center items-center px-2 py-1 rounded-lg shadow-xs focus:outline-none border-gray-300 dark:border-gray-600 border"
                           :class="{
                            'bg-white  dark:bg-gray-600' : searchGroup !== selectedSearchGroup,
                            'bg-red-700 dark:bg-bg-red-700' : searchGroup === selectedSearchGroup
                            }"
                    >
                        <input v-model="selectedSearchGroup" type="radio"
                               class="text-gray-400 form-radio focus:border-gray-400 focus:outline-none focus:shadow-none dark:focus:shadow-none"
                               name="survey-type" :value="searchGroup">
                        <span class="ml-2 text-md font-semibold"
                              :class="{
                            'text-gray-600 dark:text-gray-300' : searchGroup !== selectedSearchGroup,
                            'text-white dark:text-white': searchGroup === selectedSearchGroup
                        }">
                    {{ searchGroupName }}</span>
                    </label>
                </div>
            </div>

            <div class="mb-6 overflow-auto">
                <div v-if="loading" class="px-6 py-4 flex items-center justify-center">
                    <svg class="animate-spin h-8 w-8 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                         viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>

                <div v-else-if="errorMessage !== ''" class="px-6 py-4">
                    <p>{{ errorMessage }}</p>
                </div>

                <div v-else class="px-6 py-4 grid grid-cols-2 lg:grid-cols-3 gap-6">
                    <div
                        class="border dark:border-gray-500 rounded-md flex items-center flex-col justify-between p-3 cursor-pointer"
                        v-for="quoteBlock in blocks"
                        :key="quoteBlock.id"
                        @click="templateSelected(quoteBlock)"
                    >
                        <img :src="quoteBlock.preview_image_url" alt="" class="mb-3">
                        <p class="text-md font-medium text-gray-600 dark:text-gray-300">{{ quoteBlock.name }}</p>
                    </div>
                </div>
            </div>
            <footer
                class="flex flex-col items-center justify-end px-6 py-3 sm:flex-row bg-gray-50 dark:bg-gray-700"
            >
                <jet-button
                    @click="close"
                    type="button"
                    class="w-full px-5 py-3 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg sm:w-auto sm:px-4 sm:py-2 active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                >
                    Cancel
                </jet-button>
            </footer>
        </div>
    </div>
    <!-- End of modal backdrop -->
</template>

<script>
import {computed, watch, ref, nextTick, reactive} from "vue";
import JetInput from '@/Jetstream/Input';
import JetButton from '@/Jetstream/Button';

export default {
    components: {
        JetInput,
        JetButton
    },
    props: {
        isModalOpen: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, {emit}) {
        const loadingBlockTypes = ref(false);
        const loadingCustomBlocks = ref(false);
        const allBlocks = reactive({
            blockTypes: [],
            blocks: [],
        });
        let errorMessage = ref('');
        const searchInput = ref(null);

        function getBlocks() {
            loadingBlockTypes.value = true;
            loadingCustomBlocks.value = true;
            axios
                .get(route('admin.api.quote-block-type.index'))
                .then(response => {
                    allBlocks.blockTypes = response.data.data;
                })
                .catch(error => {
                    errorMessage.value = error.message
                })
                .finally(() => {
                    loadingBlockTypes.value = false;
                })

            axios
                .get(route('admin.api.quote-block.index'))
                .then(response => {
                    allBlocks.blocks = response.data.data;
                })
                .catch(error => {
                    errorMessage.value = error.message
                })
                .finally(() => {
                    loadingCustomBlocks.value = false;
                })
        }

        const loading = computed(() => loadingBlockTypes.value || loadingCustomBlocks.value);
        const blocks = computed(() => {
            let filtered = {};
            if (search) {
                if (["all", "customBlocks"].includes(selectedSearchGroup.value)) {
                    filtered.blocks = allBlocks.blocks.filter((block) => _.lowerCase(block.name).includes(_.lowerCase(search.value)));
                }
                if (["all", "blockTypes"].includes(selectedSearchGroup.value)) {
                    filtered.blockTypes = allBlocks.blockTypes.filter((block) => _.lowerCase(block.name).includes(_.lowerCase(search.value)));
                }
            } else {
                filtered = allBlocks;
            }

            switch (selectedSearchGroup.value) {
                case 'all':
                    return [...filtered.blocks, ...filtered.blockTypes];
                case 'customBlocks':
                    return [...filtered.blocks];
                case 'blockTypes':
                    return [...filtered.blockTypes];
            }
        });

        const close = () => {
            search.value = '';
            emit("close");
        }

        const templateSelected = (quoteBlock) => {
            emit("templateSelected", quoteBlock);
            close();
        }

        const search = ref('');

        const isModalShown = computed(() => props.isModalOpen);
        watch(isModalShown, (newValue) => {
            if (newValue) {
                getBlocks();
                nextTick(() => {
                    searchInput.value.focus();
                })
            }
        });

        const blockSearchGroup = {
            all: "All",
            blockTypes: "Block types",
            customBlocks: "Custom Blocks"
        }

        const selectedSearchGroup = ref("all");

        return {
            loading,
            errorMessage,
            blocks,
            getBlocks,
            close,
            templateSelected,
            search,
            searchInput,
            blockSearchGroup,
            selectedSearchGroup
        }
    },
}
</script>

<style scoped>

</style>
